import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRightIcon as ArrowRight, DogIcon as Dog, PlusIcon as Plus, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import DogLogLanding from "./DogLogLanding";
import CareTab from "@/components/dog-detail/CareTab";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";

/**
 * Smart `/dog-log` route.
 *
 * Resolves to one of four states:
 *   1. **Not logged in** → render the marketing `DogLogLanding`
 *      component (free to view, CTAs route through `/login?intent=dog-log`).
 *   2. **Logged in but owns no dogs** → friendly empty state nudging
 *      them to add their first dog.
 *   3. **Logged in, has dogs, no Dog Log access** → still render
 *      `DogLogLanding` so the upsell + sample-day preview is visible.
 *   4. **Logged in, has dogs, Dog Log active** → render the functional
 *      log surface: a dog-picker dropdown across the top, and the
 *      existing `CareTab` component rendered for the selected dog.
 *
 * Selected dog persists in `?dog_id=…` so refreshes & shared URLs keep
 * the picker in sync.
 */
export default function DogLogHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Not logged in → always show the marketing landing.
  if (!user) return <DogLogLanding />;

  return <AuthenticatedHub navigate={navigate} searchParams={searchParams} setSearchParams={setSearchParams} />;
}

function AuthenticatedHub({ navigate, searchParams, setSearchParams }) {
  const [status, setStatus] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedId = searchParams.get("dog_id");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/care/status"),
      api.get("/dogs?limit=100"),
    ])
      .then(([s, d]) => {
        if (cancelled) return;
        setStatus(s.data || {});
        // /dogs returns either a list or {dogs:[]} depending on endpoint variant.
        const rows = Array.isArray(d.data) ? d.data : (d.data?.dogs || []);
        // Only show living dogs; deceased dogs stay in the dog detail view.
        const livingOwned = rows.filter((dg) => !dg.deceased_at);
        setDogs(livingOwned);
        // Auto-pick the first dog if no `dog_id` query param is set.
        if (livingOwned.length > 0 && !selectedId) {
          setSearchParams({ dog_id: livingOwned[0].id }, { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) setStatus({ active: false });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <p className="text-sm text-[#6B6B6B]">Loading Dog Log…</p>
        </div>
      </AppShell>
    );
  }

  // Logged in but doesn't own any dogs yet → first-run nudge.
  if (dogs.length === 0) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-[#FBF7EC] to-[#FFFDF6] p-10 text-center" data-testid="dog-log-empty-state">
            <div className="h-16 w-16 rounded-full bg-white border border-[#E8D9A6] flex items-center justify-center mx-auto mb-5 shadow-[0_8px_24px_-12px_rgba(199,162,39,0.45)]">
              <Dog size={28} weight="duotone" className="text-gold-dark" />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-3">
              Dog Log · Step 1
            </p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-3">
              Add your first dog to start logging.
            </h1>
            <p className="text-[#3F3F3F] text-[15px] leading-relaxed max-w-xl mx-auto mb-7">
              Dog Log organizes meals, walks, weight, meds, and symptoms by dog. Add your pup (just name &amp; breed will do — you can fill in details later) and you're off.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Button
                onClick={() => navigate("/dogs/new")}
                className="rounded-full bg-ink hover:bg-crimson text-white px-6 h-11"
                data-testid="dog-log-add-dog-btn"
              >
                <Plus size={15} weight="bold" className="mr-1.5" /> Add my first dog
              </Button>
              <Link to="/dog-log/about" className="text-sm text-[#6B6B6B] hover:text-ink underline underline-offset-4">
                What is Dog Log?
              </Link>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // Has dogs but no Dog Log subscription → marketing landing + checkout
  // (DogLogLanding handles its own auth state and checkout flow).
  if (!status?.active) {
    return <DogLogLanding />;
  }

  // Active subscriber with dogs → real logging surface.
  const selectedDog =
    dogs.find((d) => d.id === selectedId) || dogs[0];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16">
        {/* Header */}
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2 flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Dog Log
            </p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight text-ink">
              Today with {selectedDog?.name || "your dog"}
            </h1>
            <p className="text-[#6B6B6B] text-sm mt-1">
              Log meals, walks, weight, meds, symptoms — and share live with your sitter.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/dogs/new")}
            className="rounded-full h-10"
            data-testid="dog-log-add-another"
          >
            <Plus size={14} className="mr-1" /> Add another dog
          </Button>
        </div>

        {/* Dog picker */}
        <div className="mb-6 flex items-center gap-3" data-testid="dog-log-picker-row">
          <label className="text-[12px] font-medium text-[#3F3F3F] whitespace-nowrap">
            Logging for:
          </label>
          <Select
            value={selectedDog?.id || ""}
            onValueChange={(v) => setSearchParams({ dog_id: v })}
          >
            <SelectTrigger
              className="w-full max-w-md rounded-full bg-white border-[#E5E5E5] h-12 px-3"
              data-testid="dog-log-picker"
            >
              <SelectValue>
                {selectedDog ? <DogPickerRow dog={selectedDog} /> : "Pick a dog"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {dogs.map((dog) => (
                <SelectItem key={dog.id} value={dog.id} data-testid={`dog-log-picker-option-${dog.id}`}>
                  <DogPickerRow dog={dog} compact />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link
            to={`/dogs/${selectedDog?.id}`}
            className="text-[12px] text-[#6B6B6B] hover:text-ink whitespace-nowrap"
            data-testid="dog-log-view-profile"
          >
            View profile <ArrowRight size={10} className="inline ml-0.5" weight="bold" />
          </Link>
        </div>

        {/* Care surface — re-uses the existing fully-featured CareTab
            component from the dog detail page. `isOwnerOrAdmin` is true
            here because the API already filtered to dogs the user
            owns. */}
        {selectedDog && (
          <CareTab key={selectedDog.id} dog={selectedDog} isOwnerOrAdmin />
        )}
      </div>
    </AppShell>
  );
}

function DogPickerRow({ dog, compact = false }) {
  const photo = dog.photos?.[0];
  return (
    <span className="flex items-center gap-2.5 min-w-0">
      <span className={`flex-shrink-0 rounded-full bg-cream overflow-hidden ring-1 ring-[#E2DAC2] ${compact ? "h-7 w-7" : "h-8 w-8"}`}>
        {photo ? (
          <img src={thumbUrl(photo)} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center">
            <Dog size={compact ? 13 : 15} weight="duotone" className="text-[#6B6B6B]" />
          </span>
        )}
      </span>
      <span className="min-w-0 text-left">
        <span className="block font-spectral text-[15px] tracking-tight truncate">{dog.name}</span>
        {dog.breed && (
          <span className="block text-[11px] text-[#6B6B6B] truncate">{dog.breed}</span>
        )}
      </span>
    </span>
  );
}
